import type { BottleUnit } from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { ChartColumn, Menu, Moon, NotebookPen, Settings2, Sparkles, Sun, X } from "lucide-react";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { useNight } from "../../lib/night.tsx";
import { dateline } from "../../lib/time.ts";
import { cn } from "../../lib/utils.ts";
import { Button } from "../ui/button.tsx";
import { AssistantTip } from "./assistant-tip.tsx";

/**
 * The app's pages, in menu order. A new page goes here and nowhere else: on a
 * phone it joins the menu, on a wide screen the row of links. The assistant
 * isn't one of them: it keeps its own sparkle (and the tip that points at it).
 */
const PAGES = [
  { to: "/", label: "Log", icon: NotebookPen },
  { to: "/overview", label: "Overview", icon: ChartColumn },
  { to: "/settings", label: "Settings", icon: Settings2 },
] as const;

/**
 * The masthead, set as a dictionary entry: the headword in syllables, its
 * pronunciation between slashes (from a 400px phone up).
 * Read aloud as "babia, pronounced ba-bee-uh".
 */
function Headword() {
  return (
    <h1 className="flex min-w-0 items-baseline gap-x-2 whitespace-nowrap">
      <span className="heading text-[28px] tracking-[-0.02em] md:text-[32px]">
        <span aria-hidden="true">ba·bi·a</span>
        <span className="sr-only">babia</span>
      </span>
      {/* Under 400px (a 390px iPhone or a 360px Android) the controls need the room. */}
      <span
        aria-hidden="true"
        className="hidden text-[18px] italic opacity-70 min-[400px]:inline md:text-[20px]"
      >
        /ba-BEE-uh/
      </span>
      <span className="sr-only">, pronounced ba-bee-uh</span>
    </h1>
  );
}

/**
 * The broadsheet head: the masthead with the controls on the right, then the
 * thick rule, the rail (this page's name on the left, the dateline on the
 * right), and the thin rule. The first control is the sparkle, in the second
 * spot colour so it stands out: connecting an AI assistant (/assistant). Then
 * the pages (`PAGES`): a row of links from 1024px up, and below that a menu
 * button that opens them as a list under the masthead. The current page is
 * underlined. `tip` hangs the first-time assistant tip under the sparkle.
 */
export function LogHeader({
  now,
  view = "log",
  rail,
  tip,
}: {
  now: Date;
  view?: "log" | "overview";
  /** More for the rail after the page name (the overview's "updated" line). */
  rail?: React.ReactNode;
  tip?: {
    open: boolean;
    babyName: string;
    unit: BottleUnit;
    onAnswer: (accepted: boolean) => void;
  };
}) {
  const { night, toggle } = useNight();
  const controls = useRef<HTMLDivElement>(null);
  const sparkle = useRef<HTMLAnchorElement>(null);
  const [caretRight, setCaretRight] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const tipOpen = tip?.open ?? false;
  // The tip's caret points at the sparkle, wherever the tablet labels put it.
  useLayoutEffect(() => {
    if (!tipOpen) return;
    const measure = () => {
      const box = controls.current?.getBoundingClientRect();
      const icon = sparkle.current?.getBoundingClientRect();
      if (box && icon) setCaretRight(box.right - (icon.left + icon.width / 2));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [tipOpen]);
  const linkClass =
    "ghost-btn inline-flex h-11 min-w-11 items-center justify-center gap-2 px-2.5 text-ink";
  const label = "hidden text-[14px] md:inline";
  const current = "aria-[current=page]:underline aria-[current=page]:underline-offset-[6px]";
  return (
    <header>
      <div className="flex items-center gap-2">
        <Headword />
        <div ref={controls} className="relative ml-auto flex shrink-0 items-center">
          <Link
            ref={sparkle}
            to="/assistant"
            className={cn(linkClass, "text-accent-2 hover:bg-accent-2-tint")}
            aria-label="Talk to your log with an AI assistant"
            title="AI assistant"
          >
            <Sparkles className="size-5" />
            <span className={label}>AI</span>
          </Link>
          <nav aria-label="Pages" className="hidden lg:flex">
            {PAGES.map(({ to, label: name, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                activeOptions={{ exact: true }}
                className={cn(linkClass, current)}
              >
                <Icon className="size-5" />
                <span className="text-[14px]">{name}</span>
              </Link>
            ))}
          </nav>
          <Button
            variant="icon"
            onClick={toggle}
            aria-label={night ? "Switch to day mode" : "Switch to night mode"}
            title={night ? "Day mode" : "Night mode"}
          >
            {night ? <Sun className="size-5" /> : <Moon className="size-5" />}
          </Button>
          <Button
            variant="icon"
            className="lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            title="Menu"
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
          {tip?.open && !menuOpen ? (
            <AssistantTip
              caretRight={caretRight}
              babyName={tip.babyName}
              unit={tip.unit}
              onAnswer={tip.onAnswer}
            />
          ) : null}
        </div>
      </div>
      {menuOpen ? (
        <nav id={menuId} aria-label="Pages" className="mt-1.5 lg:hidden">
          {PAGES.map(({ to, label: name, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              activeOptions={{ exact: true }}
              onClick={() => setMenuOpen(false)}
              className={cn(
                "ghost-btn heading flex min-h-12 items-center gap-3 border-t border-divider px-1 text-[18px] text-ink",
                current,
              )}
            >
              <Icon className="size-5 shrink-0" />
              {name}
            </Link>
          ))}
        </nav>
      ) : null}
      <div className="mt-1.5 h-[3px] bg-ink" />
      <div className="kicker flex items-baseline gap-3 py-1 leading-none">
        <span className="min-w-0 truncate">
          {view === "log" ? "The log" : "Overview"}
          {rail ? <> · {rail}</> : null}
        </span>
        <span className="ml-auto shrink-0">{dateline(now)}</span>
      </div>
      <div className="h-px bg-ink opacity-90" />
    </header>
  );
}
